import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence, useDragControls, type PanInfo } from 'framer-motion';
import { cn } from '@shared/lib/cn';

/**
 * Bottom-anchored sheet. Slides up 260ms with `smooth` easing; top corners
 * 24px radius + grabber pill. Scrim fades in 180ms.
 *
 * Swipe-to-dismiss: drag the grabber / header downwards — past ~120px or
 * with a quick flick the sheet closes (and `onOpenChange(false)` fires).
 * The scrollable body is not a drag surface, so content still scrolls.
 *
 * Backed by Radix Dialog for focus management, escape-to-dismiss and
 * `role="dialog"` semantics.
 */
interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  /** Lock to a specific peak height (CSS length). Default = content height. */
  height?: string;
  /** Optional explicit callback for grabber/header drag dismissals. */
  onDragDismiss?: () => void;
  /** Called after the sheet has fully animated out. */
  onExitComplete?: () => void;
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  height,
  onDragDismiss,
  onExitComplete,
}: BottomSheetProps): JSX.Element {
  const dragControls = useDragControls();

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    if (info.offset.y > 120 || info.velocity.y > 600) {
      onDragDismiss?.();
      onOpenChange(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence onExitComplete={onExitComplete}>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.div
                className={cn(
                  'fixed inset-x-0 bottom-0 z-50 flex max-h-[92dvh] flex-col rounded-t-2xl bg-elevated paper-grain shadow-sheet pb-safe',
                )}
                style={height ? { height } : undefined}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.55 }}
                onDragEnd={handleDragEnd}
              >
                {/* Grabber — drag handle */}
                <div
                  onPointerDown={(e) => dragControls.start(e)}
                  className="flex shrink-0 cursor-grab touch-none items-center justify-center pt-2.5 pb-2 active:cursor-grabbing"
                >
                  <span className="block h-1.5 w-10 rounded-pill bg-hairline-strong" aria-hidden />
                </div>
                {title || description ? (
                  <header
                    onPointerDown={(e) => dragControls.start(e)}
                    className="shrink-0 cursor-grab touch-none px-6 pb-3 hairline-b active:cursor-grabbing"
                  >
                    {title ? (
                      <Dialog.Title asChild>
                        <h2 className="font-display text-headline text-ink">{title}</h2>
                      </Dialog.Title>
                    ) : null}
                    {description ? (
                      <Dialog.Description asChild>
                        <p className="text-body-sm text-ink-secondary mt-1">{description}</p>
                      </Dialog.Description>
                    ) : null}
                  </header>
                ) : null}
                <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4">{children}</div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}
